/*
 * Copyright (c) 2021 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Tuesday, December 7th 2021, 9:19:51 am
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import React from "react";
import TextField, { TextFieldProps } from "@mui/material/TextField";
import Chip from "@mui/material/Chip";

type TagsInputProps = TextFieldProps & {
  defaultValues?: string[];
  onValuesChange?: (values: string[]) => void;
  validator?: (value: string) => boolean;
};

export const TagsInput = (props: TagsInputProps) => {
  const { validator = null, defaultValues = [], onValuesChange = null } = props;
  const [values, setValues] = React.useState(defaultValues);
  const [controlledTextValue, setControlledTextValue] = React.useState("");

  React.useEffect(() => onValuesChange?.(values), [values, onValuesChange]);

  const updateValues = (value: string) => {
    const trimmed_value = value.trim();
    if (trimmed_value && (!validator || validator(trimmed_value))) {
      setValues((previous) => [...previous, trimmed_value]);
    }
    setControlledTextValue("");
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (["Enter", "Tab", " "].includes(e.key)) {
      updateValues((e.target as HTMLInputElement).value);
    }
  };

  const handleBlur = (e: React.SyntheticEvent) => {
    updateValues((e.target as HTMLInputElement).value);
  };

  const handleDelete = (value) => {
    setValues((previous) => previous.filter((v) => v !== value));
  };

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setControlledTextValue(e.target.value);
  };

  return (
    <TextField
      {...props}
      InputProps={{
        startAdornment: (
          <>
            {props.InputProps?.startAdornment}
            {values.map((value, index) => (
              <Chip
                className="os-tags-chip"
                key={`${index}-${value}`}
                tabIndex={-1}
                label={value}
                onDelete={() => handleDelete(value)}
              />
            ))}
          </>
        ),
      }}
      value={controlledTextValue}
      onKeyDown={handleKeyDown}
      onBlur={handleBlur}
      onChange={onChange}
    />
  );
};
